import { create } from "zustand";
import { Progress, UpdateFn, Updater } from "../../types";

interface State {
  progresses: Progress[];
  baseId: number;
  progressMap: Record<string, number | null>;
}

interface Action {
  setter: <T extends keyof State>(key: T, updater: Updater<State[T]>) => void;
  setProgresses: UpdateFn<Progress[]>;
  updateProgress: (progress: Progress) => void;
  rmProgress: (filename: string) => void;
  getProgress: (filename: string) => Progress | null;

  getId: () => number;

  setProgressMap: UpdateFn<Record<string, number | null>>;
  progressMapAdd: (filename: string) => number;
  progressMapRm: (filename: string) => void;
  getProgressId: (filename: string) => number | null;
}

const useProgressStore = create<State & Action>((set, get) => ({
  progresses: [],
  baseId: 10,
  progressMap: {},

  setter: <T extends keyof State>(key: T, updater: Updater<State[T]>): void => {
    if (typeof updater === "function") {
      set((state) => ({ [key]: updater(state[key]) }));
    } else {
      set({ [key]: updater });
    }
  },

  setProgresses: (progresses) => {
    get().setter("progresses", progresses);
  },

  setProgressMap: (progressMap) => {
    get().setter("progressMap", progressMap);
  },

  updateProgress: (progress) => {
    get().setProgresses((prev) => {
      for (let i = 0; i < prev.length; i++) {
        if (prev[i].id !== progress.id) continue;
        prev[i] = progress;
        return [...prev];
      }
      return [...prev, progress];
    });
  },

  rmProgress: (filename) => {
    let id = get().getProgressId(filename);
    if (!id) return;

    get().setProgresses((prev) => {
      prev = prev.filter((p) => p.id !== id);
      return [...prev];
    });

    get().progressMapRm(filename);
  },

  getProgress: (filename) => {
    let id = get().getProgressId(filename);
    if (!id) return null;

    const res = get().progresses.filter((p) => p.id === id);
    return res.length ? res[0] : null;
  },

  getId: () => {
    let res = get().baseId;
    set({ baseId: res + 1 });
    return res;
  },

  progressMapAdd: (filename) => {
    let id = get().getId();
    get().setProgressMap((prev) => ({ ...prev, [filename]: id }));
    return id;
  },

  progressMapRm: (filename) => {
    get().setProgressMap((prev) => ({ ...prev, [filename]: null }));
  },

  getProgressId: (filename) => {
    return get().progressMap[filename];
  },
}));

export default useProgressStore;
